import { Injectable } from '@angular/core';
import { environment } from '@environments/environment';
import { SelectedAccountStore } from '../stores/selected-account-store.service';
import { FeatureFlag } from '@app/shared/models/feature-flag';
import { ConfigCatWrapper } from '../utils/configcat-wrapper';
import { AuthenticationService } from '../authentication/authentication.service';
import { WindowRefService } from '@core/services/helpers/window-ref.service';
import { ACCOUNT_PERMISSION_LEVEL } from '@app/shared/enum/permissions';

@Injectable()
export class FeatureFlagService {
    private configCatClient: any;

    private loggerLevel = {
        off: -1,
        error: 1,
        warn: 2,
        info: 3,
    };

    constructor(
        private selectedAccountStore: SelectedAccountStore,
        private authenticationService: AuthenticationService,
        private windowRefService: WindowRefService
    ) {}

    initialize() {
        if (environment.configcat.enabled) {
            this.configCatClient = ConfigCatWrapper.createClient(environment.configcat.key, {
                pollIntervalSeconds: 600,
                logger: ConfigCatWrapper.createConsoleLogger(
                    environment.production ? this.loggerLevel.off : this.loggerLevel.info
                ),
            });
        }
    }

    async getAllFeaturesFlags(
        accountPermissionLevel: ACCOUNT_PERMISSION_LEVEL = ACCOUNT_PERMISSION_LEVEL.ALL
    ): Promise<FeatureFlag[]> {
        let result: FeatureFlag[] = [];

        if (this.configCatClient) {
            const featuresFlags = await this.configCatClient.getAllValuesAsync(
                this.getTargetObject(accountPermissionLevel)
            );
            result = featuresFlags.map((featureFlag) => ({
                feature: featureFlag.settingKey,
                value: featureFlag.settingValue,
            }));
        }

        return result;
    }

    async isFeatureEnabled(
        flag: string,
        defaultValue = true,
        customRules?: any,
        accountPermissionLevel: ACCOUNT_PERMISSION_LEVEL = ACCOUNT_PERMISSION_LEVEL.ALL
    ): Promise<boolean> {
        const target = this.getTargetObject(accountPermissionLevel, customRules);
        let isEnabled = defaultValue;

        if (this.configCatClient) {
            isEnabled = await this.configCatClient.getValueAsync(flag, defaultValue, target);
        }

        return isEnabled;
    }

    private getTargetObject(
        accountPermissionLevel: ACCOUNT_PERMISSION_LEVEL,
        customRules?: any
    ): any {
        let identifier = this.selectedAccountStore.selectedAccount.id.toString();
        if (
            accountPermissionLevel === ACCOUNT_PERMISSION_LEVEL.SERVICE_ACCOUNT &&
            this.authenticationService.user.serviceAccount
        ) {
            identifier = this.authenticationService.user.serviceAccount.id.toString();
        }
        return {
            identifier,
            custom: {
                user: this.authenticationService.user.id.toString(),
                domain: this.getDomain(),
                ...customRules,
            },
        };
    }

    private getDomain(): string {
        const hostDomain = this.windowRefService.window.location.hostname
            .split('.')
            .filter((item: string) => item !== 'www')[0];

        const environments = {
            internalDev: {
                environment: 'internaldev',
                subdomains: ['internaldev', 'localhost'],
            },
            beta: {
                environment: 'beta',
                subdomains: ['beta'],
            },
            prod: {
                environment: 'prod',
                subdomains: [],
            },
        };

        let currentEnvironment = Object.values(environments).reduce((env, value) => {
            if (value.subdomains.some((subdomain) => hostDomain.startsWith(subdomain))) {
                env = value.environment;
            }
            return env;
        }, '');

        if (!currentEnvironment) {
            currentEnvironment = environments.prod.environment;
        }

        return currentEnvironment;
    }
}
